import { useEffect, useRef } from 'react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { cn } from '../../lib/utils';
import { categoricalPallete } from '../themes/colors';

export type TGroupedHistogramOptions = {
  title?: string;
  colors?: string[];
  yAxisTitle?: string;
  tooltipFormatter?: (this: any) => string;
  xAxisFormatter?: (ctx: any) => string;
  onClick?: (e: any) => void;
  unselect?: boolean;
  height?: number;
  width?: number;
  yAxisCategories?: string[];
  yAxisMax?: number;
  xAxisTitle?: string;
  lineWidth?: number;
  plotLines?: {
    value?: number;
    dashStyle?: 'Dash' | 'Dot' | 'Solid';
  }[];
};

export type TBucket = {
  label?: string;
  width: number;
  height?: number;
  color: string;
  textColor: string;
};

export const GroupedHistogram = ({
  data,
  chartOptions,
  buckets,
  size,
  numberOfHistogramBuckets,
  clippedPercentage,
}: {
  data: { name: string; data: number[] }[];
  chartOptions: TGroupedHistogramOptions;
  buckets?: TBucket[];
  size?: 'small' | 'medium';
  numberOfHistogramBuckets?: number;
  clippedPercentage?: number;
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      style: {
        fontFamily: 'Inter',
      },
      backgroundColor: 'transparent',
      height: chartOptions.height,
      width: chartOptions.width,
      margin: size === 'small' ? [5, 20, 48, 20] : [5, 20, 80, 20],
    },
    title: {
      text: chartOptions.title ?? '',
    },
    plotOptions: {
      column: {
        pointPadding: 0,
        groupPadding: 0.05,
        borderWidth: 0,
        borderRadius: 2,
        events: {
          click: chartOptions.onClick,
        },
      },
    },
    yAxis: {
      startOnTick: false,
      endOnTick: false,
      max: chartOptions.yAxisMax,
      min: 0,
      categories: chartOptions.yAxisCategories,
      labels: {
        style: {
          fontSize: '12px',
        },
        rotation: 0,
        enabled: chartOptions.yAxisCategories !== undefined,
      },
      title: {
        text: chartOptions.yAxisTitle ?? '',
      },
      gridLineWidth: 0,
    },
    xAxis: {
      min: 0,

      title: {
        text: chartOptions.xAxisTitle ?? '',
      },
      gridLineWidth: 0,
      labels: {
        align: 'right',
        formatter: chartOptions.xAxisFormatter,
        style: {
          fontSize: '12px',
        },
        useHTML: true,
        y: size === 'small' ? 35 : 50,
      },
      tickLength: 0,
      lineWidth: chartOptions.lineWidth ?? 1,
      plotLines: chartOptions.plotLines?.map((line) => ({
        width: 2,
        color: 'black',
        dashStyle: line.dashStyle,
        zIndex: 5,
        value:
          line.value !== undefined
            ? (Math.min(line.value, clippedPercentage ?? 1) *
                (numberOfHistogramBuckets ?? 10)) /
                (clippedPercentage ?? 1) -
              0.5
            : undefined,
      })),
    },
    colors: chartOptions.colors ?? categoricalPallete,
    legend: {
      enabled: false,
    },
    tooltip: {
      formatter: chartOptions.tooltipFormatter,
      useHTML: true,
    },
    series: data.map((series) => ({
      ...series,
      type: 'column',
    })),
    credits: {
      enabled: false,
    },
  };

  useEffect(() => {
    chartRef.current?.chart.reflow();
  }, []);

  return (
    <div className="vai-relative vai-w-full">
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
      />
      <div
        className={cn(
          'vai-absolute vai-flex vai-flex-row vai-left-0 vai-gap-[1px] vai-w-full vai-px-5',
          size === 'small' && 'vai-h-4 vai-bottom-[30px]',
          size === 'medium' && 'vai-h-7 vai-bottom-[50px]',
        )}
      >
        {buckets
          ?.filter((bucket) => bucket.width > 0)
          ?.map((bucket, index) => (
            <div
              key={index}
              className={cn(
                'vai-px-2 vai-text-sm vai-flex vai-items-center vai-h-full',
                bucket.color,
                bucket.textColor,
                index === 0 && 'vai-rounded-l-[4px]',
                index === buckets?.filter((b) => b.width > 0).length - 1 &&
                  'vai-rounded-r-[4px]',
              )}
              style={{
                width: `${bucket.width * 100}%`,
                height: bucket.height,
              }}
            >
              <span className="vai-truncate">{bucket.label}</span>
            </div>
          ))}
      </div>
    </div>
  );
};
